const VALID_SLUG_CHARS_REGEX = /^[a-z0-9\-]+$/;

export function roughNumberFormat(n: number): string {
  // If number is divisible by 10, return the number, e.g. 40 => "40"
  if (n % 10 === 0) {
    return n.toString();
  }

  // If the number is not divisible by 10, round the number down to nearest tens
  // and display "N0+", e.g. 43 => "40+"
  return `${Math.floor(n / 10) * 10}+`;
}

export function dumbSlugifySearchQuery(originalQuery: string): string {
  return escape(originalQuery.replace(/\s/g, '+'));
}

/** Convert a string to a slug (i.e. convert to lowercase, remove invalid chars) */
export function slugify(str: string): string {
  return str
    .split('')
    .map(s => (s === ' ' ? '-' : s.toLowerCase()))
    .filter(s => VALID_SLUG_CHARS_REGEX.test(s))
    .join('');
}

/** Check if a string is a valid slug (i.e. only contains chars, numbers, and dashes) */
export function isValidSlug(str: string): boolean {
  return VALID_SLUG_CHARS_REGEX.test(str);
}

export function commatize(n: number): string {
  const formatted = Math.abs(n)
    .toString()
    .split('')
    .reverse()
    .map((digit, index) => (index > 0 && index % 3 === 0 ? `${digit},` : digit))
    .reverse()
    .join('');
  return n >= 0 ? formatted : `-${formatted}`;
}

export function stripHtmlTags(html: string): string {
  return html.replace(/<(?:.|\n)*?>/gm, '');
}

/** Ensure a string is unique by appending a random string to the end of it */
export function randomString(baseString: string): string {
  const suffix = Math.random().toString(36).slice(2);
  return `${baseString}-${suffix}`;
}

/**
 * Ensure that a string is unique by appending `-N` to the string. This function
 * _always_ applies the `-N` suffix.
 */
export function indexedUniqueString(str: string, index: number, existingStrs: string[]): string {
  const modifiedStr = `${str}-${index}`;
  if (existingStrs.filter(existing => existing === modifiedStr).length !== 0) {
    return indexedUniqueString(str, index + 1, existingStrs);
  }
  return modifiedStr;
}

/**
 * Ensure that a string is unique in comparison to a list of other strings. This will append `-N`
 * to the string or increment the suffix there already is a trailing `-N`. Returns the original if
 * it's already unique.
 */
export function uniqueString(str: string, existingStrs: string[]): string {
  if (existingStrs.filter(existing => existing === str).length !== 0) {
    const match = str.match(/^(.*)-(\d+)$/);
    if (match) {
      return indexedUniqueString(match[1], parseInt(match[2], 10) + 1, existingStrs);
    }
    return indexedUniqueString(str, 1, existingStrs);
  }
  return str;
}

/** Capitalize the first letter of a string (and uncapitalize the rest of the string) */
export function capitalize(str: string): string {
  return `${str[0].toUpperCase()}${str.substr(1).toLowerCase()}`;
}
